import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Camera,
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Check,
  Image as ImageIcon,
} from 'lucide-react';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: string;
  onDocumentUploaded: (data: { url: string; name: string }) => void;
  currentDocumentUrl?: string | null;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  documentType,
  onDocumentUploaded,
  currentDocumentUrl,
}) => {
  const [mode, setMode] = useState<'select' | 'camera' | 'preview'>('select');
  const [capturedImage, setCapturedImage] = useState<string | null>(currentDocumentUrl || null);
  const [fileName, setFileName] = useState<string>(`${documentType}_document.jpg`);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stop camera stream cleanly
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  // Start live camera stream
  const startCamera = async () => {
    setCameraError(null);
    setMode('camera');
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Rear camera preferred for documents, fallback to any
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCameraActive(true);
    } catch (err: any) {
      console.warn('Camera access failed, trying user camera', err);
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
        streamRef.current = fallbackStream;
        if (videoRef.current) {
          videoRef.current.srcObject = fallbackStream;
          await videoRef.current.play();
        }
        setIsCameraActive(true);
      } catch (fallbackErr: any) {
        setCameraError(
          fallbackErr?.message?.includes('Permission') || fallbackErr?.name === 'NotAllowedError'
            ? 'Camera permission was denied. Please allow camera access in your browser or select a file instead.'
            : 'Could not access camera on this device. Please upload a document file instead.'
        );
        setIsCameraActive(false);
      }
    }
  };

  // Capture frame from camera
  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    stopCamera();
    setCapturedImage(dataUrl);
    setFileName(`${documentType}_photo_${Date.now()}.jpg`);
    setMode('preview');
  };

  // Handle file input selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target?.result as string);
        setMode('preview');
      };
      reader.readAsDataURL(file);
    } else {
      // PDF or other document
      setCapturedImage('pdf_placeholder');
      setMode('preview');
    }
  };

  // Confirm and close
  const handleConfirm = () => {
    if (!capturedImage) return;
    onDocumentUploaded({
      url: capturedImage,
      name: fileName,
    });
    handleClose();
  };

  const handleClose = () => {
    stopCamera();
    setMode('select');
    setCameraError(null);
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div
      id="document-upload-modal"
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-lg border border-slate-200 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
                Upload {documentType} Document
              </h3>
              <p className="text-xs text-slate-500">
                Camera দিয়ে ছবি তুলুন অথবা ফাইল ব্রাউজ করে আপলোড করুন
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Hidden native file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Modal Body */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* MODE: SELECT OPTION (Camera vs File) */}
          {mode === 'select' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-600">
                Please provide a clear, readable copy of your original <strong>{documentType}</strong>. All government information is strictly encrypted.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                {/* Option 1: Live Camera */}
                <button
                  type="button"
                  id="btn-doc-open-camera"
                  onClick={startCamera}
                  className="group p-5 rounded-2xl border-2 border-slate-200 hover:border-purple-600 bg-slate-50/50 hover:bg-purple-50/40 transition-all text-left flex flex-col items-center sm:items-start text-center sm:text-left cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-md shadow-purple-600/20 group-hover:scale-105 transition-transform mb-3">
                    <Camera className="w-6 h-6" />
                  </div>
                  <span className="font-extrabold text-slate-900 text-sm block">
                    Use Camera (ক্যামেরা)
                  </span>
                  <span className="text-[11px] text-slate-500 mt-1 block">
                    Snap a live photo of your physical ID card using your device camera
                  </span>
                </button>

                {/* Option 2: File Browser */}
                <button
                  type="button"
                  id="btn-doc-browse-file"
                  onClick={() => fileInputRef.current?.click()}
                  className="group p-5 rounded-2xl border-2 border-slate-200 hover:border-pink-600 bg-slate-50/50 hover:bg-pink-50/40 transition-all text-left flex flex-col items-center sm:items-start text-center sm:text-left cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-2xl bg-pink-600 text-white flex items-center justify-center shadow-md shadow-pink-600/20 group-hover:scale-105 transition-transform mb-3">
                    <Upload className="w-6 h-6" />
                  </div>
                  <span className="font-extrabold text-slate-900 text-sm block">
                    Browse File (ফাইল)
                  </span>
                  <span className="text-[11px] text-slate-500 mt-1 block">
                    Select a scanned image or document from your phone or computer
                  </span>
                </button>
              </div>

              {cameraError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{cameraError}</span>
                </div>
              )}

              <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-[11px]">
                  Supports JPG, PNG, WebP or PDF • Document is encrypted & secure
                </span>
              </div>
            </div>
          )}

          {/* MODE: LIVE CAMERA CAPTURE */}
          {mode === 'camera' && (
            <div className="space-y-4">
              <div className="relative aspect-[4/3] bg-black rounded-2xl overflow-hidden flex items-center justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />

                {/* Document Alignment Frame */}
                <div className="absolute inset-6 border-2 border-white/80 rounded-xl pointer-events-none flex flex-col justify-between p-3">
                  <div className="flex justify-between">
                    <div className="w-4 h-4 border-t-2 border-l-2 border-purple-400" />
                    <div className="w-4 h-4 border-t-2 border-r-2 border-purple-400" />
                  </div>
                  <div className="text-center">
                    <span className="px-3 py-1 bg-black/60 text-white text-[11px] font-bold rounded-full backdrop-blur-xs">
                      Align {documentType} inside this frame
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <div className="w-4 h-4 border-b-2 border-l-2 border-purple-400" />
                    <div className="w-4 h-4 border-b-2 border-r-2 border-purple-400" />
                  </div>
                </div>

                {cameraError && (
                  <div className="absolute inset-0 bg-slate-900/90 flex items-center justify-center p-4 text-center">
                    <div className="space-y-2 text-white">
                      <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
                      <p className="text-xs">{cameraError}</p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-purple-600 rounded-xl text-xs font-bold"
                      >
                        Choose File Instead
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    stopCamera();
                    setMode('select');
                  }}
                  className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  id="btn-doc-snap"
                  onClick={capturePhoto}
                  className="flex-2 py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Camera className="w-4 h-4" />
                  <span>Snap Photo</span>
                </button>
              </div>
            </div>
          )}

          {/* MODE: PREVIEW & CONFIRM */}
          {mode === 'preview' && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="relative aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 flex items-center justify-center">
                {capturedImage && capturedImage !== 'pdf_placeholder' ? (
                  <img
                    src={capturedImage}
                    alt="Document preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-6 space-y-2">
                    <FileText className="w-16 h-16 text-purple-600 mx-auto" />
                    <p className="font-bold text-slate-800 text-sm">{fileName}</p>
                    <span className="text-[11px] text-emerald-600 font-bold bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                      PDF Document Ready
                    </span>
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-slate-900/80 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg backdrop-blur-xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{fileName}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCapturedImage(null);
                    setMode('select');
                  }}
                  className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Retake / Change</span>
                </button>

                <button
                  type="button"
                  id="btn-doc-confirm"
                  onClick={handleConfirm}
                  className="flex-2 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Check className="w-4 h-4" />
                  <span>Use This Document</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
